import { useState, useEffect } from 'react';
import { Music, Plus, Trash2, Edit2, Save, X, Loader, GripVertical } from 'lucide-react';
import { supabase, Playlist, PlaylistTrack } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export function PlaylistManager() {
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState<Playlist | null>(null);
  const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTracks, setLoadingTracks] = useState(false);

  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDesc, setNewPlaylistDesc] = useState('');

  const [newTrackUrl, setNewTrackUrl] = useState('');
  const [newTrackTitle, setNewTrackTitle] = useState('');

  useEffect(() => {
    if (user) {
      loadPlaylists();
    }
  }, [user]);

  useEffect(() => {
    if (selectedPlaylist) {
      loadTracks(selectedPlaylist.id);
    }
  }, [selectedPlaylist]);

  const loadPlaylists = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('playlists')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPlaylists(data || []);
    } catch (err) {
      console.error('Error loading playlists:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTracks = async (playlistId: string) => {
    setLoadingTracks(true);
    try {
      const { data, error } = await supabase
        .from('playlist_tracks')
        .select('*')
        .eq('playlist_id', playlistId)
        .order('position', { ascending: true });

      if (error) throw error;
      setTracks(data || []);
    } catch (err) {
      console.error('Error loading tracks:', err);
    } finally {
      setLoadingTracks(false);
    }
  };

  const createPlaylist = async () => {
    if (!user || !newPlaylistName.trim()) return;

    try {
      const { data, error } = await supabase
        .from('playlists')
        .insert({
          user_id: user.id,
          name: newPlaylistName,
          description: newPlaylistDesc,
          is_public: true
        })
        .select()
        .single();

      if (error) throw error;

      setPlaylists([data, ...playlists]);
      setNewPlaylistName('');
      setNewPlaylistDesc('');
      setIsCreating(false);
    } catch (err) {
      console.error('Error creating playlist:', err);
    }
  };

  const deletePlaylist = async (playlistId: string) => {
    if (!confirm('Delete this playlist? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('playlists')
        .delete()
        .eq('id', playlistId);

      if (error) throw error;

      setPlaylists(playlists.filter(p => p.id !== playlistId));
      if (selectedPlaylist?.id === playlistId) {
        setSelectedPlaylist(null);
        setTracks([]);
      }
    } catch (err) {
      console.error('Error deleting playlist:', err);
    }
  };

  const addTrack = async () => {
    if (!selectedPlaylist || !newTrackUrl.trim() || !newTrackTitle.trim()) return;

    try {
      const { data, error } = await supabase
        .from('playlist_tracks')
        .insert({
          playlist_id: selectedPlaylist.id,
          youtube_url: newTrackUrl,
          title: newTrackTitle,
          position: tracks.length
        })
        .select()
        .single();

      if (error) throw error;

      setTracks([...tracks, data]);
      setNewTrackUrl('');
      setNewTrackTitle('');
    } catch (err) {
      console.error('Error adding track:', err);
    }
  };

  const deleteTrack = async (trackId: string) => {
    try {
      const { error } = await supabase
        .from('playlist_tracks')
        .delete()
        .eq('id', trackId);

      if (error) throw error;

      setTracks(tracks.filter(t => t.id !== trackId));
    } catch (err) {
      console.error('Error deleting track:', err);
    }
  };

  const moveTrack = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= tracks.length) return;

    const newTracks = [...tracks];
    [newTracks[index], newTracks[targetIndex]] = [newTracks[targetIndex], newTracks[index]];

    setTracks(newTracks);

    try {
      const updates = newTracks.map((track, idx) => ({
        id: track.id,
        position: idx
      }));

      for (const update of updates) {
        await supabase
          .from('playlist_tracks')
          .update({ position: update.position })
          .eq('id', update.id);
      }
    } catch (err) {
      console.error('Error reordering tracks:', err);
      loadTracks(selectedPlaylist!.id);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Music className="w-6 h-6 text-[#00c7b7]" />
        <h2 className="text-2xl font-bold">Playlist Manager</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Your Playlists</h3>
            <button
              onClick={() => setIsCreating(!isCreating)}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] text-sm"
            >
              <Plus className="w-4 h-4" />
              New
            </button>
          </div>

          {isCreating && (
            <div className="mb-4 p-4 border border-blue-200 rounded-lg bg-teal-50">
              <input
                type="text"
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                placeholder="Playlist name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
              />
              <textarea
                value={newPlaylistDesc}
                onChange={(e) => setNewPlaylistDesc(e.target.value)}
                placeholder="Description"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
              />
              <div className="flex gap-2">
                <button
                  onClick={createPlaylist}
                  disabled={!newPlaylistName.trim()}
                  className="flex-1 px-3 py-1.5 bg-[#00c7b7] text-white rounded-lg hover:bg-[#14f0d5] disabled:opacity-50"
                >
                  Create
                </button>
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setNewPlaylistName('');
                    setNewPlaylistDesc('');
                  }}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-[#00c7b7]" />
            </div>
          ) : playlists.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500">No playlists yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {playlists.map(playlist => (
                <div
                  key={playlist.id}
                  onClick={() => setSelectedPlaylist(playlist)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedPlaylist?.id === playlist.id
                      ? 'border-[#00c7b7] bg-teal-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{playlist.name}</h4>
                      {playlist.description && (
                        <p className="text-sm text-gray-600 mt-1">{playlist.description}</p>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deletePlaylist(playlist.id);
                      }}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">
            {selectedPlaylist ? `Tracks (${tracks.length})` : 'Select a Playlist'}
          </h3>

          {selectedPlaylist ? (
            <>
              <div className="mb-4 p-4 border border-green-200 rounded-lg bg-green-50">
                <input
                  type="text"
                  value={newTrackTitle}
                  onChange={(e) => setNewTrackTitle(e.target.value)}
                  placeholder="Track title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                />
                <input
                  type="text"
                  value={newTrackUrl}
                  onChange={(e) => setNewTrackUrl(e.target.value)}
                  placeholder="YouTube URL"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-2"
                />
                <button
                  onClick={addTrack}
                  disabled={!newTrackTitle.trim() || !newTrackUrl.trim()}
                  className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Track
                </button>
              </div>

              {loadingTracks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-[#00c7b7]" />
                </div>
              ) : tracks.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">No tracks yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {tracks.map((track, index) => (
                    <div key={track.id} className="p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{track.title}</p>
                          <p className="text-xs text-gray-500 truncate">{track.youtube_url}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => moveTrack(index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-xs bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            ↑
                          </button>
                          <button
                            onClick={() => moveTrack(index, 'down')}
                            disabled={index === tracks.length - 1}
                            className="p-1 text-xs bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            ↓
                          </button>
                          <button
                            onClick={() => deleteTrack(track.id)}
                            className="p-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Select a playlist to manage tracks</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
